import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSlidersH } from '@fortawesome/free-solid-svg-icons';
import { indexOf, sumBy, startCase, toLower } from 'lodash';

import { Card, CardBody, Col, Row, Input, Tooltip, Collapse, UncontrolledTooltip } from 'reactstrap';
import Switch from 'react-switch';
import PieChart, { RISK_COLORS } from '../../common/PieChart';

// Fallback colors for categories not in RISK_COLORS
const FALLBACK_COLORS = ['#253746', '#4a8f99', '#5ba08a', '#7eb26d', '#d4a73a', '#cf7e3c', '#b94a48', '#7b4a7a'];
export default function RiskScore({ bugRisk, setBugRisk, riskError, loading }) {
  const [isOpenTooltip, setIsOpenTooltip] = useState({});
  const [showVariables, setShowVariables] = useState();

  const toggleTooltip = (e, id) => {
    // console.log('e: ', e);
    setIsOpenTooltip(e.type === 'mouseover' ? id : '');
    // setTimeout(() => {
    //   setIsOpenTooltip('');
    // }, 2500);
  };

  const handleSwitchToggle = (e, option) => {
    const options = [...bugRisk];
    const optionIndex = bugRisk.findIndex(item => item.label === option.label);
    const others = bugRisk.filter(y => {
      return indexOf(y) !== optionIndex && y.active;
    });
    const othersSum = sumBy(others, risk => {
      return risk.value;
    });
    console.log('others sum', othersSum);
    const diff = othersSum + option.value - 100;

    // Add update value of option to 0 if set to disabled
    options[optionIndex] = {
      ...options[optionIndex],
      active: e === true ? 1 : 0,
      value: othersSum + option.value > 100 && !option.active ? option.value - diff : option.value
    };
    setBugRisk(options);
  };

  const changeRisk = (i, y, value) => {
    setBugRisk(prevState => {
      prevState[i].variables[y].score = parseInt(value);
      return [...prevState];
    });
  };

  const changeShare = (e, i) => {
    const num = e.target.value;
    // Add all other values of active matrices
    const others = bugRisk.filter(y => {
      return i !== bugRisk.indexOf(y) && y.active;
    });
    const othersSum = sumBy(others, risk => {
      return risk.value;
    });
    // Get max value for current filter
    if (num <= 100 - othersSum)
      setBugRisk(prevState => {
        prevState[i].value = parseInt(num);
        return [...prevState];
      });
    if (num < 0)
      setBugRisk(prevState => {
        prevState[i].value = 0;
        return [...prevState];
      });
  };

  const toggleVariables = (e, variables) => {
    e.preventDefault();
    variables === showVariables ? setShowVariables('') : setShowVariables(variables);
  };

  const getColorForCategory = (label, index) => {
    return RISK_COLORS[label] || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
  };

  // Calculate bug risk score (average of active categories weighted by their values)
  const calculateBugRiskScore = () => {
    const activeRisks = bugRisk?.filter(b => b?.active) || [];
    if (!activeRisks.length) return 0;

    let totalScore = 0;
    let totalWeight = 0;

    activeRisks.forEach(risk => {
      if (risk.variables?.length) {
        const avgVariableScore = risk.variables.reduce((sum, v) => sum + (v.score || 0), 0) / risk.variables.length;
        totalScore += (avgVariableScore / 100) * risk.value;
      } else {
        totalScore += risk.value;
      }
      totalWeight += risk.value;
    });

    return totalWeight > 0 ? ((totalScore / totalWeight) * 10).toFixed(2) : 0;
  };

  return (
    <>
      <Card className="mb-4">
        <CardBody>
          <Row>
            <Col lg="5">
              <h5 className="mb-3">
                Bug Risk Score{' '}
                <span id="risk-score-preview" style={{ cursor: 'help' }}>
                  ({calculateBugRiskScore()})
                </span>
                <UncontrolledTooltip target="risk-score-preview" placement="right">
                  Preview score (0–10): weighted average of active category variable scores. Final score is calculated
                  per managed product based on its actual bug data.
                </UncontrolledTooltip>
              </h5>
              {bugRisk?.filter(b => b?.active).length ? (
                <PieChart data={bugRisk?.filter(b => b?.active)} />
              ) : (
                <div className="d-flex align-items-center justify-content-center" style={{ height: '350px' }}>
                  <h5 className="text-muted">No enabled categories</h5>
                </div>
              )}
            </Col>
            <Col lg="7">
              <Row className="mb-2 px-2" style={{ fontSize: '0.85rem', fontWeight: 600, color: '#5e6e82' }}>
                <Col xs="2" className="text-center" style={{ whiteSpace: 'nowrap' }}>
                  Enabled
                </Col>
                <Col xs="1" className="text-center" style={{ whiteSpace: 'nowrap' }}>
                  Color
                </Col>
                <Col xs="5" style={{ whiteSpace: 'nowrap' }}>
                  Category
                </Col>
                <Col xs="4" className="text-center" style={{ whiteSpace: 'nowrap' }}>
                  Risk Share (%)
                </Col>
              </Row>

              {bugRisk?.length
                ? bugRisk.map((item, i) => {
                    const cleanLabel =
                      item?.label === 'priority'
                        ? item?.label?.replace('priority', 'severity')
                        : item?.label?.replaceAll('_', ' ');
                    const categoryColor = getColorForCategory(item.label, i);

                    return (
                      <React.Fragment key={i + item?.label + '-label-key'}>
                        <Row className="align-items-center py-2 px-2 border-bottom">
                          <Col xs="2" className="text-center">
                            <Switch
                              id={item?.label + '-switch'}
                              onChange={e => handleSwitchToggle(e, item)}
                              checked={item?.active === 1}
                              uncheckedIcon={false}
                              checkedIcon={false}
                              onColor="#253746"
                              offColor="#d8e2ef"
                              height={20}
                              width={40}
                              handleDiameter={16}
                            />
                          </Col>
                          <Col xs="1" className="text-center">
                            <div
                              style={{
                                width: '24px',
                                height: '24px',
                                backgroundColor: categoryColor,
                                borderRadius: '4px'
                              }}
                            />
                          </Col>
                          <Col xs="5">
                            <div id={item.label + '-target'} className="d-flex align-items-center">
                              <span style={{ fontWeight: 500 }}>
                                {/* startCase lowercases acronyms (e.g. "Odd"); restore ODD. */}
                                {startCase(toLower(cleanLabel)).replace(/\bOdd\b/g, 'ODD')}
                              </span>
                              {item?.description?.length ? (
                                <>
                                  <FontAwesomeIcon
                                    icon="question-circle"
                                    className="fs--2 ml-1 text-primary"
                                    id="view-setting-tooltip"
                                  />
                                  <Tooltip
                                    placement="top"
                                    isOpen={isOpenTooltip === item.label + '-target'}
                                    target={item.label + '-target'}
                                    autohide={true}
                                    toggle={e => toggleTooltip(e, item.label + '-target')}
                                  >
                                    {item.description}
                                  </Tooltip>
                                </>
                              ) : null}
                            </div>
                          </Col>
                          <Col xs="4" className="d-flex align-items-center justify-content-center">
                            <Input
                              type="number"
                              value={bugRisk[i]?.value}
                              onChange={e => changeShare(e, i)}
                              style={{ width: '70px', textAlign: 'center' }}
                              disabled={item?.active !== 1}
                            />
                            {item?.variables?.length ? (
                              <FontAwesomeIcon
                                icon={faSlidersH}
                                className={`ml-2 ${item?.active === 1 ? 'text-primary cursor-pointer' : 'text-muted'}`}
                                style={{ fontSize: '1.1rem', cursor: item?.active === 1 ? 'pointer' : 'default' }}
                                onClick={e => (item?.active === 1 ? toggleVariables(e, item?.label) : null)}
                              />
                            ) : (
                              <div style={{ width: '24px' }} />
                            )}
                          </Col>
                        </Row>

                        <Collapse isOpen={showVariables === item?.label && item?.variables?.length > 0}>
                          <div className="bg-light py-2 px-3 border-bottom">
                            {item?.variables?.map((sub, y) => (
                              <Row
                                key={y + sub?.label + '-variable-key'}
                                className="align-items-center py-1"
                                style={{ fontSize: '0.85rem' }}
                              >
                                <Col xs="4">
                                  <span
                                    id={`var-${item.label}-${y}-target`}
                                    className="d-flex align-items-center text-muted"
                                  >
                                    {sub?.label}
                                    {sub?.description?.length ? (
                                      <>
                                        <FontAwesomeIcon icon="question-circle" className="fs--2 ml-1 text-primary" />
                                        <Tooltip
                                          placement="top"
                                          isOpen={isOpenTooltip === `var-${item.label}-${y}-target`}
                                          target={`var-${item.label}-${y}-target`}
                                          autohide={true}
                                          toggle={e => toggleTooltip(e, `var-${item.label}-${y}-target`)}
                                        >
                                          {sub?.description}
                                        </Tooltip>
                                      </>
                                    ) : null}
                                  </span>
                                </Col>
                                <Col xs="6">
                                  <Input
                                    type="range"
                                    className="custom-range"
                                    value={sub?.score}
                                    min="0"
                                    max="100"
                                    onChange={e => changeRisk(i, y, e.target?.value)}
                                    style={{ accentColor: categoryColor }}
                                  />
                                </Col>
                                <Col xs="2" className="text-right text-muted">
                                  {sub?.score}/100
                                </Col>
                              </Row>
                            ))}
                          </div>
                        </Collapse>
                      </React.Fragment>
                    );
                  })
                : null}

              <p className="mt-3 mb-0 text-muted" style={{ fontSize: '0.85rem', fontStyle: 'italic' }}>
                Note: The total Risk Share percentage must add up to 100%
              </p>
              {riskError?.length ? (
                <p className="text-danger mt-1 mb-0" style={{ fontSize: '0.85rem' }}>
                  Error: {riskError}
                </p>
              ) : null}
            </Col>
          </Row>
        </CardBody>
      </Card>
    </>
  );
}

RiskScore.propTypes = {
  bugRisk: PropTypes.array,
  riskError: PropTypes.string,
  setBugRisk: PropTypes.func,
  loading: PropTypes.bool
};
